/**
 * §M1 · `NavContext` re-scoped to ONE specific organization.
 *
 * `useNavContext` always answers for the switcher's ACTIVE org — perfect
 * for painting the sidebar, useless for "Mis perfiles", which has to
 * explain the N academies at once without forcing the user to switch one at a
 * time. This hook reuses the global context (session, platform role,
 * teacher, guardian) and replaces what IS per-organization: the membership
 * role and feature resolution.
 *
 * It also returns `blockerOf`, the graph key that actually blocks a
 * feature: it can be a REQUIREMENT of it, and saying "missing X" when the culprit is
 * its dependency sends the user to fight the wrong switch.
 */
import { type Accessor, createMemo } from "solid-js";
import { featureGraph, parseFeatureOverrides, resolveFeatureEffective } from "@space/shared";

import type { NavContext } from "@/features/navigation/nav-model";
import { useNavContext } from "@/features/navigation/use-nav-context";
import { useOrgSettings } from "@/features/academy/hooks/use-academy-data";
import { useMyOrgRole } from "@/features/academy/hooks/use-people-data";
import { usePlatformFlags } from "@/features/flags/use-feature-enabled";

export interface OrgNavContext {
  context: Accessor<NavContext>;
  /** The graph key blocking `key` (or `undefined` if it's enabled). */
  blockerOf: (key: string) => string | undefined;
}

export function useOrgNavContext(orgId: Accessor<string | undefined>): OrgNavContext {
  const base = useNavContext();
  const myRole = useMyOrgRole(orgId);
  const platformFlags = usePlatformFlags();
  const orgSettings = useOrgSettings(orgId);
  const graph = featureGraph();

  const resolver = createMemo(() => {
    const platformMap = platformFlags();
    const org = parseFeatureOverrides(orgSettings.data?.features);
    return (key: string) => resolveFeatureEffective(key, { platformMap, org }, graph);
  });

  const context = createMemo<NavContext>(() => ({
    ...base(),
    orgRole: myRole.role(),
    featureEnabled: (key: string) => resolver()(key).enabled,
  }));

  return { context, blockerOf: (key) => resolver()(key).blockedBy };
}
